import { Injectable, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { HelperDebugLoggerControllerService } from './helper-debug-logger-controller.service';

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root',
})
export class HelperAnalyticsControllerService {
  private router = inject(Router);
  private debugLogger = inject(HelperDebugLoggerControllerService);

  public readonly isInitialized = signal<boolean>(false);

  constructor() {
    this.init();
  }

  /**
   * Initializes Google Analytics 4 (GA4) and starts router view tracking
   */
  public init(): void {
    if (this.isInitialized()) {
      return;
    }

    const measurementId = environment.gaMeasurementId;

    if (measurementId && typeof document !== 'undefined') {
      this.loadGtagScript(measurementId);
    }

    // Auto-track screen navigation events across all personas
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.trackPageView(document?.title || environment.app, event.urlAfterRedirects || event.url);
      });

    this.isInitialized.set(true);
  }

  /**
   * Logs a custom event to Google Analytics and the internal debug logger
   */
  public logEvent(eventName: string, params: Record<string, any> = {}): void {
    const enrichedParams: Record<string, any> = {
      ...params,
      app_persona: environment.app,
      instance_tier: environment.instance,
      platform: Capacitor.getPlatform(),
      timestamp: new Date().toISOString(),
    };

    // Forward to GA4 / gtag if allowed
    if (environment.allowGoogleAnalyticsLog && typeof window !== 'undefined' && typeof window.gtag === 'function') {
      try {
        window.gtag('event', eventName, enrichedParams);
      } catch (err) {
        console.warn('⚠️ GA4 logEvent error:', err);
      }
    }

    this.debugLogger.addLog('info', `[GA4] ${eventName}`, enrichedParams);
  }

  /**
   * Tracks screen/page view
   */
  public trackPageView(pageTitle: string, pagePath: string): void {
    this.logEvent('page_view', {
      page_title: pageTitle,
      page_location: typeof window !== 'undefined' ? window.location.href : pagePath,
      page_path: pagePath,
    });
  }

  private loadGtagScript(measurementId: string): void {
    if (typeof document === 'undefined') return;

    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    if (!window.gtag) {
      window.gtag = function () {
        window.dataLayer?.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, { send_page_view: false });
    }

    const scriptId = 'ga4-gtag-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);
    }
  }
}
